/**
 * Life4Earth V2 — Media Links API Adapter
 *
 * CRUD operations for the media_links table.
 * Used by Progress Gallery (Impact tab), User Profile Gallery,
 * and future Gantt task media attachments.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from './client';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type MediaEntityType =
  | 'project'
  | 'mini_project'
  | 'user'
  | 'gantt_task'
  | 'community';
export type MediaType = 'photo' | 'video' | 'document' | 'photo_pack';
export type MediaVisibility = 'public' | 'contributors' | 'private';

/** Where this gallery item comes from. "media_links" = a real row in the
 *  media_links table (editable, deletable). "status" = a virtual projection
 *  of a mini-project status photo, surfaced for display only — the Media
 *  tab should hide edit/delete affordances and show a backlink to the
 *  owning status. */
export type MediaLinkSource = 'media_links' | 'status';

/**
 * Matches the camelCase JSON returned by ASP.NET Core's MediaLinkResponse DTO.
 */
export interface MediaLinkDto {
  mediaLinkGuid: string;
  entityType: MediaEntityType;
  entityGuid: string;
  mediaType: MediaType;
  storageKey: string;
  thumbnailKey: string | null;
  url: string | null;
  label: string | null;
  description: string | null;
  startSeconds: number | null;
  sortOrder: number;
  fileSizeBytes: number | null;
  contentType: string | null;
  widthPx: number | null;
  heightPx: number | null;
  visibility: MediaVisibility;
  tags: string[] | null;
  uploadedByGuid: string | null;
  createdAt: string;
  updatedAt: string;
  /** DEV-1079: backing source. Defaults to "media_links" — older API
   *  builds that don't emit this field round-trip as undefined; treat
   *  undefined as "media_links" downstream. */
  source?: MediaLinkSource;
  /** Set only when source === "status". GUID of the owning status. */
  sourceStatusGuid?: string | null;
  /** Set only when source === "status". Reporting date of the owning
   *  status (YYYY-MM-DD), used for the "from {date} status" backlink. */
  sourceReportingDate?: string | null;
}

export interface CreateMediaLinkRequest {
  entityType: MediaEntityType;
  entityGuid: string;
  mediaType: MediaType;
  storageKey: string;
  thumbnailKey?: string | null;
  label?: string | null;
  description?: string | null;
  startSeconds?: number | null;
  fileSizeBytes?: number | null;
  contentType?: string | null;
  widthPx?: number | null;
  heightPx?: number | null;
  visibility?: MediaVisibility;
  tags?: string[] | null;
}

/* ------------------------------------------------------------------ */
/*  API Functions                                                      */
/* ------------------------------------------------------------------ */

/**
 * Fetch all media links for an entity (e.g., all photos for a project).
 * Returns newest-first by created_at.
 */
export async function fetchMediaLinks(
  entityType: MediaEntityType,
  entityGuid: string,
  mediaType?: MediaType,
  limit: number = 100,
  offset: number = 0
): Promise<MediaLinkDto[]> {
  const params = new URLSearchParams();
  if (mediaType) params.set('mediaType', mediaType);
  if (limit !== 100) params.set('limit', String(limit));
  if (offset > 0) params.set('offset', String(offset));
  const qs = params.toString();
  return apiFetch<MediaLinkDto[]>(
    `/api/MediaLinks/${entityType}/${entityGuid}${qs ? `?${qs}` : ''}`
  );
}

/**
 * Create a new media link record.
 * Call this AFTER uploading the file to MinIO via uploadFile().
 */
export async function createMediaLink(
  req: CreateMediaLinkRequest
): Promise<MediaLinkDto> {
  return apiFetch<MediaLinkDto>('/api/MediaLinks', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

/**
 * Update a media link (e.g., edit caption).
 */
export async function updateMediaLink(
  mediaLinkGuid: string,
  updates: { label?: string; description?: string; visibility?: MediaVisibility }
): Promise<MediaLinkDto> {
  return apiFetch<MediaLinkDto>(`/api/MediaLinks/${mediaLinkGuid}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Soft-delete a media link and hard-delete the stored file.
 */
export async function deleteMediaLink(
  mediaLinkGuid: string
): Promise<void> {
  return apiFetch<void>(`/api/MediaLinks/${mediaLinkGuid}`, {
    method: 'DELETE',
  });
}

/* ------------------------------------------------------------------ */
/*  Admin Stats                                                        */
/* ------------------------------------------------------------------ */

export interface MediaStats {
  totalFiles: number;
  totalBytes: number;
}

/** Fetch platform-wide media stats (admin console). */
export async function fetchMediaStats(): Promise<MediaStats> {
  return apiFetch<MediaStats>('/api/MediaLinks/stats');
}

/** Format bytes into a human-readable string (e.g. "2.3 GB"). */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = bytes / Math.pow(1024, i);
  return `${val < 10 ? val.toFixed(1) : Math.round(val)} ${units[i]}`;
}

/** Build a display label like "1,247 files · 2.3 GB". */
export function formatMediaStatsLabel(stats: MediaStats): string {
  const files = stats.totalFiles.toLocaleString();
  const size = formatBytes(stats.totalBytes);
  return `${files} files · ${size}`;
}

/**
 * React hook for admin console — fetches platform-wide media stats.
 * Returns loading/source/countLabel compatible with ServiceStatus.
 */
export function useMediaStats() {
  const [stats, setStats] = useState<MediaStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<'api' | 'none' | 'loading'>('loading');
  const mountedRef = useRef(true);

  const load = useCallback(() => {
    setLoading(true);
    setSource('loading');
    fetchMediaStats()
      .then((data) => {
        if (!mountedRef.current) return;
        setStats(data);
        setSource('api');
        setLoading(false);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setSource('none');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => { mountedRef.current = false; };
  }, [load]);

  const countLabel = stats ? formatMediaStatsLabel(stats) : undefined;

  return { stats, loading, source, countLabel, refetch: load };
}
